import {commitUpdateIssueSubscriptionMutation} from '@github-ui/issue-viewer/commitUpdateIssueSubscriptionMutation'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {addReactionMutation} from '@github-ui/reaction-viewer/addReactionMutation'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {useDebounce} from '@github-ui/use-debounce'
import {useSafeTimeout} from '@primer/react'
import {startTransition, useCallback, useEffect, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LowQualityWarningBanner} from '../components/LowQualityWarningBanner'
import bannerStyles from '../components/LowQualityWarningBanner.module.css'
import {ERRORS} from '../constants/errors'
import {REACTION_CONTENT, SUBSCRIPTION_STATE} from '../constants/graphql-enums'
import {getLowQualityFragments, isLowQualityComment, isNegativeReactionComment} from '../utils/low-quality-comment'

const ANALYTICS_TARGET = 'LOW_QUALITY_COMMENT_WARNING'

const AnalyticsEvent = {
  Shown: 'comments.low_quality_warning.shown',
  Dismissed: 'comments.low_quality_warning.dismissed',
  Liked: 'comments.low_quality_warning.liked',
  LikeFailed: 'comments.low_quality_warning.like_failed',
  Subscribed: 'comments.low_quality_warning.subscribed',
  Unsubscribed: 'comments.low_quality_warning.unsubscribed',
  SubmittedWithWarning: 'comments.low_quality_warning.submitted_with_warning',
  CommentImproved: 'comments.low_quality_warning.comment_improved',
} as const

type AnalyticsEvent = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]

const AutoSubscribeOutcome = {
  Skipped: 'skipped',
  Success: 'success',
  Failed: 'failed',
} as const

export const LowQualityWarningState = {
  Idle: 'idle',
  ShowingLikeAction: 'showing-like-action',
  ShowingSubscribeAction: 'showing-subscribe-action',
  ShowingNoAction: 'showing-no-action',
  Reacting: 'reacting',
  LikedAutoSubscribing: 'liked-auto-subscribing',
  LikedAndSubscribed: 'liked-and-subscribed',
  LikedOnly: 'liked-only',
  Subscribing: 'subscribing',
  Subscribed: 'subscribed',
  Unsubscribing: 'unsubscribing',
  Unsubscribed: 'unsubscribed',
  Resubscribing: 'resubscribing',
  Dismissed: 'dismissed',
} as const

export type LowQualityWarningState = (typeof LowQualityWarningState)[keyof typeof LowQualityWarningState]

/** States where the warning banner is actively showing */
const SHOWING_STATES: ReadonlySet<LowQualityWarningState> = new Set([
  LowQualityWarningState.ShowingLikeAction,
  LowQualityWarningState.ShowingSubscribeAction,
  LowQualityWarningState.ShowingNoAction,
])

/** Minimum length to check for low-quality comments. Shortest realistic low-quality comment is 2 chars (e.g., "+1", "ok"). */
const MIN_LENGTH_FOR_LOW_QUALITY_CHECK = 2

/** Debounce delay for low-quality comment detection (ms) */
export const LOW_QUALITY_CHECK_DEBOUNCE_MS = 1000

/** Debounce delay for re-checking if comment quality has improved (ms) */
export const QUALITY_RECHECK_DEBOUNCE_MS = 500

/** Duration for fade-out animation (ms) var(--base-duration-200) */
export const BANNER_ANIMATION_LENGTH = 200

/** Delay before starting fade-out after the comment quality improves (ms) */
export const AUTO_DISMISS_DELAY = 2000

/**
 * Determines the initial warning state based on viewer's existing like/subscribe status.
 *
 * The logic prioritizes showing the most useful action:
 * - If viewer hasn't liked AND comment isn't negative → show "Like" action
 * - If viewer has liked OR comment is negative (liking doesn't make sense) →
 *   - If not subscribed → show "Subscribe" action
 *   - If already subscribed → show message only (no actions)
 */
function getInitialWarningState({
  viewerHasLiked,
  viewerIsSubscribed,
  isNegativeReaction,
}: {
  viewerHasLiked: boolean
  viewerIsSubscribed: boolean
  isNegativeReaction: boolean
}): LowQualityWarningState {
  // Skip "like" action if viewer already liked or if comment is negative (e.g., "-1", "👎")
  const shouldSkipLikeAction = viewerHasLiked || isNegativeReaction

  if (!shouldSkipLikeAction) {
    return LowQualityWarningState.ShowingLikeAction
  }

  if (!viewerIsSubscribed) {
    return LowQualityWarningState.ShowingSubscribeAction
  }

  return LowQualityWarningState.ShowingNoAction
}

interface UseLowQualityWarningOptions {
  /** The markdown content to check for low-quality patterns */
  markdownBody: string
  /** The Relay node ID of the issue - required for like/subscribe actions */
  issueId: string
  /** Whether the viewer has already liked (thumbsup) this issue */
  viewerHasLiked: boolean
  /** Whether the viewer is already subscribed to this issue */
  viewerIsSubscribed: boolean
  /** Whether the viewer's like/subscribe data has loaded (delays banner until ready) */
  isViewerDataLoaded?: boolean
  /** Optional callback invoked when the warning banner is dismissed */
  onDismiss?: () => void
}

interface UseLowQualityWarningResult {
  /** The banner element to render, or undefined if not showing */
  banner: React.ReactNode | undefined
  /** CSS class to apply to a banner ancestor — adds textarea bottom padding on mobile to prevent overlap */
  bannerPaddingClassName: string | undefined
  /** Whether the warning banner is currently visible to the user */
  isBannerVisible: boolean
  /**
   * Call this when the comment is submitted to track if the user ignored the warning.
   * Fires an analytics event only if the warning was shown during this session.
   * @param currentBody - The current comment body at time of submission
   */
  trackSubmittedWithWarning: (currentBody: string) => void
}

/**
 * Hook that detects low-quality comments and returns a warning banner.
 *
 * The banner actions depend on the viewer's existing state:
 * - Not liked + Not subscribed: Like → Auto-subscribe → Show Unsubscribe
 * - Not liked + Subscribed: Like only → Done
 * - Liked + Not subscribed: Subscribe → Show Unsubscribe
 * - Liked + Subscribed: No actions, just message
 *
 * @example
 * ```tsx
 * const { banner } = useLowQualityWarning({
 *   markdownBody,
 *   issueId: issue.id,
 *   viewerHasLiked,
 *   viewerIsSubscribed,
 *   onDismiss: () => commentBoxRef.current?.focus(),
 * })
 *
 * return <CommentBox infoBanner={banner} ... />
 * ```
 */
export function useLowQualityWarning({
  markdownBody,
  issueId,
  viewerHasLiked,
  viewerIsSubscribed,
  isViewerDataLoaded = true,
  onDismiss,
}: UseLowQualityWarningOptions): UseLowQualityWarningResult {
  const environment = useRelayEnvironment()
  const {sendAnalyticsEvent} = useAnalytics()
  const {addToast} = useToastContext()

  const sendLowQualityAnalytics = useCallback(
    (event: AnalyticsEvent, payload?: Record<string, string | boolean>) => {
      sendAnalyticsEvent(event, ANALYTICS_TARGET, payload)
    },
    [sendAnalyticsEvent],
  )

  // eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags
  const lowQualityWarningEnabled = useFeatureFlag('issues_react_low_quality_comment_warning')

  const [state, setState] = useState<LowQualityWarningState>(LowQualityWarningState.Idle)
  const [isFading, setIsFading] = useState(false)
  const shownRef = useRef(false)
  const triggeringFragmentsRef = useRef<string[]>([])
  const stateBeforeUnsubscribingRef = useRef<LowQualityWarningState>(LowQualityWarningState.Idle)
  // Use refs to avoid stale closures in callbacks
  const viewerIsSubscribedRef = useRef(viewerIsSubscribed)
  const stateRef = useRef(state)
  const onDismissRef = useRef(onDismiss)
  useEffect(() => {
    viewerIsSubscribedRef.current = viewerIsSubscribed
    stateRef.current = state
    onDismissRef.current = onDismiss
  }, [viewerIsSubscribed, state, onDismiss])

  const lowQualityCheckCallback = useCallback(
    (text: string) => {
      if (
        lowQualityWarningEnabled &&
        text.trim().length >= MIN_LENGTH_FOR_LOW_QUALITY_CHECK &&
        isLowQualityComment(text)
      ) {
        triggeringFragmentsRef.current = getLowQualityFragments(text)
        const initialState = getInitialWarningState({
          viewerHasLiked,
          viewerIsSubscribed,
          isNegativeReaction: isNegativeReactionComment(text),
        })
        startTransition(() => setState(initialState))
      }
    },
    [lowQualityWarningEnabled, viewerHasLiked, viewerIsSubscribed],
  )

  const checkLowQualityComment = useDebounce(lowQualityCheckCallback, LOW_QUALITY_CHECK_DEBOUNCE_MS, {
    leading: false,
    trailing: true,
  })

  useEffect(() => {
    if (state === LowQualityWarningState.Idle && markdownBody.trim().length > 0 && isViewerDataLoaded) {
      checkLowQualityComment(markdownBody)
    }
  }, [markdownBody, state, isViewerDataLoaded, checkLowQualityComment])

  // Auto-dismiss: fade banner after delay when user improves their comment
  const {safeSetTimeout, safeClearTimeout} = useSafeTimeout()
  const fadeTimerRef = useRef(0)
  const dismissTimerRef = useRef(0)
  const isAutoDismissingRef = useRef(false)
  const hasReportedImprovementRef = useRef(false)

  const startAutoDismiss = useCallback(() => {
    safeClearTimeout(fadeTimerRef.current)
    safeClearTimeout(dismissTimerRef.current)
    isAutoDismissingRef.current = true
    fadeTimerRef.current = safeSetTimeout(() => setIsFading(true), AUTO_DISMISS_DELAY)
    dismissTimerRef.current = safeSetTimeout(() => {
      setState(LowQualityWarningState.Dismissed)
      setIsFading(false)
      isAutoDismissingRef.current = false
      onDismissRef.current?.()
    }, AUTO_DISMISS_DELAY + BANNER_ANIMATION_LENGTH)
  }, [safeSetTimeout, safeClearTimeout])

  const cancelAutoDismiss = useCallback(() => {
    safeClearTimeout(fadeTimerRef.current)
    safeClearTimeout(dismissTimerRef.current)
    isAutoDismissingRef.current = false
    setIsFading(false)
  }, [safeClearTimeout])

  // Re-check quality while warning is showing (uses stateRef to avoid recreating debounce)
  const qualityRecheckCallback = useCallback(
    (text: string) => {
      const currentState = stateRef.current
      if (!SHOWING_STATES.has(currentState)) return

      const hasSubstantiveContent = text.trim().length >= MIN_LENGTH_FOR_LOW_QUALITY_CHECK
      if (!hasSubstantiveContent) return

      const isStillLowQuality = isLowQualityComment(text)

      if (!isStillLowQuality && !isAutoDismissingRef.current) {
        if (!hasReportedImprovementRef.current) {
          sendLowQualityAnalytics(AnalyticsEvent.CommentImproved)
          hasReportedImprovementRef.current = true
        }
        startAutoDismiss()
      }

      if (isStillLowQuality && isAutoDismissingRef.current) {
        cancelAutoDismiss()
      }
    },
    [sendLowQualityAnalytics, startAutoDismiss, cancelAutoDismiss],
  )

  const recheckQuality = useDebounce(qualityRecheckCallback, QUALITY_RECHECK_DEBOUNCE_MS, {
    leading: false,
    trailing: true,
  })

  useEffect(() => {
    if (SHOWING_STATES.has(state)) {
      recheckQuality(markdownBody)
    }
  }, [markdownBody, state, recheckQuality])

  const isShowingState = SHOWING_STATES.has(state)

  useEffect(() => {
    if (isShowingState && !shownRef.current) {
      shownRef.current = true
      sendLowQualityAnalytics(AnalyticsEvent.Shown)
    }
  }, [isShowingState, sendLowQualityAnalytics])

  const handleDismiss = useCallback(() => {
    sendLowQualityAnalytics(AnalyticsEvent.Dismissed)
    setState(LowQualityWarningState.Dismissed)
    onDismiss?.()
  }, [sendLowQualityAnalytics, onDismiss])

  const trackSubmittedWithWarning = useCallback(
    (currentBody: string) => {
      if (shownRef.current && triggeringFragmentsRef.current.length > 0) {
        sendLowQualityAnalytics(AnalyticsEvent.SubmittedWithWarning, {
          triggering_fragments: triggeringFragmentsRef.current.join(','),
          is_still_low_quality: isLowQualityComment(currentBody),
        })
      }
    },
    [sendLowQualityAnalytics],
  )

  const handleLikeIssue = useCallback(() => {
    setState(LowQualityWarningState.Reacting)

    addReactionMutation({
      environment,
      input: {subject: issueId, content: REACTION_CONTENT.THUMBS_UP},
      onCompleted: () => {
        // If already subscribed, we're done. Otherwise, auto-subscribe.
        // Use ref to get current value, avoiding stale closure
        if (viewerIsSubscribedRef.current) {
          setState(LowQualityWarningState.LikedOnly)
          sendLowQualityAnalytics(AnalyticsEvent.Liked, {
            autoSubscribeOutcome: AutoSubscribeOutcome.Skipped,
          })
        } else {
          // Auto-subscribe after liking
          setState(LowQualityWarningState.LikedAutoSubscribing)
          commitUpdateIssueSubscriptionMutation({
            environment,
            input: {subscribableId: issueId, state: SUBSCRIPTION_STATE.SUBSCRIBED},
            onCompleted: () => {
              setState(LowQualityWarningState.LikedAndSubscribed)
              sendLowQualityAnalytics(AnalyticsEvent.Liked, {
                autoSubscribeOutcome: AutoSubscribeOutcome.Success,
              })
            },
            onError: () => {
              // If subscribe fails, just show liked state without subscribe
              setState(LowQualityWarningState.LikedOnly)
              sendLowQualityAnalytics(AnalyticsEvent.Liked, {
                autoSubscribeOutcome: AutoSubscribeOutcome.Failed,
              })
              // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
              addToast({
                type: 'error',
                message: ERRORS.couldNotSubscribe,
              })
            },
          })
        }
      },
      onError: () => {
        setState(LowQualityWarningState.ShowingLikeAction)
        sendLowQualityAnalytics(AnalyticsEvent.LikeFailed)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotReact,
        })
      },
    })
  }, [issueId, environment, sendLowQualityAnalytics, addToast])

  const handleSubscribe = useCallback(() => {
    const isResubscribing = state === LowQualityWarningState.Unsubscribed
    setState(isResubscribing ? LowQualityWarningState.Resubscribing : LowQualityWarningState.Subscribing)

    commitUpdateIssueSubscriptionMutation({
      environment,
      input: {subscribableId: issueId, state: SUBSCRIPTION_STATE.SUBSCRIBED},
      onCompleted: () => {
        setState(LowQualityWarningState.Subscribed)
        sendLowQualityAnalytics(AnalyticsEvent.Subscribed)
      },
      onError: () => {
        // Restore to appropriate state based on where we came from
        setState(isResubscribing ? LowQualityWarningState.Unsubscribed : LowQualityWarningState.ShowingSubscribeAction)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSubscribe,
        })
      },
    })
  }, [issueId, environment, state, sendLowQualityAnalytics, addToast])

  const handleUnsubscribe = useCallback(() => {
    stateBeforeUnsubscribingRef.current = state
    setState(LowQualityWarningState.Unsubscribing)

    commitUpdateIssueSubscriptionMutation({
      environment,
      input: {subscribableId: issueId, state: SUBSCRIPTION_STATE.UNSUBSCRIBED},
      onCompleted: () => {
        sendLowQualityAnalytics(AnalyticsEvent.Unsubscribed)
        setState(LowQualityWarningState.Unsubscribed)
      },
      onError: () => {
        // Restore to the state we were in before unsubscribing
        setState(stateBeforeUnsubscribingRef.current)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotUnsubscribe,
        })
      },
    })
  }, [issueId, environment, state, sendLowQualityAnalytics, addToast])

  // Determine what to show based on state
  const showBanner = state !== LowQualityWarningState.Idle && state !== LowQualityWarningState.Dismissed

  const banner = showBanner ? (
    <LowQualityWarningBanner
      state={state}
      onDismiss={isFading ? () => {} : handleDismiss}
      onLikeIssue={handleLikeIssue}
      onSubscribe={handleSubscribe}
      onUnsubscribe={handleUnsubscribe}
      isFading={isFading}
    />
  ) : undefined

  // On mobile, add bottom padding to the textarea so the absolute-positioned banner doesn't overlap content.
  // Uses the existing --markdown-input-bottom-padding CSS custom property that the textarea respects.
  // Class stays applied during fade-out to prevent content from jumping before the banner fully disappears.
  const bannerPaddingClassName: string | undefined = showBanner ? bannerStyles.bannerPadding : undefined

  return {banner, bannerPaddingClassName, isBannerVisible: showBanner && !isFading, trackSubmittedWithWarning}
}
